import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/pages/Login";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { trackEvent } from "@/lib/api";
import { Eye, EyeSlash, UsersThree } from "@phosphor-icons/react";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#08090F] px-4 py-3 text-white placeholder:text-[#475569] focus:border-[#2DD4BF]/50 focus:outline-none focus:ring-1 focus:ring-[#2DD4BF]/30 transition-colors";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    const res = await register(form.name, form.email, form.password, ref);
    setLoading(false);
    if (res.ok) {
      trackEvent("signup_conversion", ref ? { referred: true } : {});
      toast.success("Account created.");
      navigate("/dashboard", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Become a member and access Q-Core.">
      {ref && (
        <div data-testid="referral-notice" className="mb-5 flex items-center gap-2.5 rounded-lg border border-[#2DD4BF]/25 bg-[#2DD4BF]/[0.06] px-4 py-3">
          <UsersThree size={18} className="text-[#2DD4BF]" />
          <span className="text-sm text-[#cbd5e1]">You were invited with code <span className="font-mono text-[#2DD4BF]">{ref}</span></span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <label className="block text-sm text-[#cbd5e1] mb-1.5">Full Name</label>
          <input data-testid="register-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
        </div>
        <div>
          <label className="block text-sm text-[#cbd5e1] mb-1.5">Email</label>
          <input data-testid="register-email" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
        </div>
        <div>
          <label className="block text-sm text-[#cbd5e1] mb-1.5">Password</label>
          <div className="relative">
            <input data-testid="register-password" type={show ? "text" : "password"} className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white">
              {show ? <EyeSlash size={17} /> : <Eye size={17} />}
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="register-submit" className="w-full px-6 py-3.5 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors disabled:opacity-60">
          {loading ? "Creating…" : "Create Account"}
        </button>
      </form>
      <p className="mt-4 text-xs text-[#64748B] leading-relaxed">
        By creating an account you acknowledge that trading involves risk and that license fees do not represent an
        investment or guaranteed return.
      </p>
      <p className="mt-6 text-sm text-[#94A3B8] text-center">
        Already a member?{" "}
        <Link to="/login" className="text-[#2DD4BF] hover:text-white transition-colors">Sign in</Link>
      </p>
    </AuthShell>
  );
}
